// Phase 2 Epic 1 (LT-140) — Opportunity / Deal / Pipeline types.
// Shape mirrors the FastAPI Pydantic schemas in backend/schemas.py.

export type TerminalType = 'won' | 'lost' | null;

export interface PipelineStage {
  id: string;
  pipeline_id: string;
  name: string;
  position: number;
  is_terminal: boolean;
  terminal_type: TerminalType;
  created_at: string;
  updated_at?: string | null;
}

export interface Pipeline {
  id: string;
  workspace_id: string;
  name: string;
  is_default: boolean;
  created_at: string;
  updated_at?: string | null;
  stages: PipelineStage[];
}

export interface PipelineCreate {
  name: string;
  is_default?: boolean;
}

export interface PipelineUpdate {
  name?: string;
  is_default?: boolean;
}

export interface PipelineStageCreate {
  name: string;
  position?: number;
  is_terminal?: boolean;
  terminal_type?: 'won' | 'lost' | null;
}

export interface PipelineStageUpdate {
  name?: string;
}

export interface Opportunity {
  id: string;
  workspace_id: string;
  pipeline_id: string;
  stage_id: string;
  account_id: string;
  primary_contact_id: string;
  owner_id?: string | null;
  name: string;
  amount?: number | null;
  currency?: string | null;
  close_date?: string | null;
  source?: string | null;
  created_at: string;
  updated_at?: string | null;
  closed_at?: string | null;
  deleted_at?: string | null;
  activity_timeline_url?: string | null;
  // §10.6 derived state — server computes from `opportunity_events`.
  is_open: boolean;
  current_cycle: number;
}

export interface OpportunityCreate {
  name: string;
  account_id: string;
  primary_contact_id: string;
  pipeline_id?: string;
  stage_id?: string;
  owner_id?: string | null;
  amount?: number | null;
  currency?: string | null;
  close_date?: string | null;
  source?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface OpportunityUpdate {
  name?: string;
  stage_id?: string;
  owner_id?: string | null;
  amount?: number | null;
  currency?: string | null;
  close_date?: string | null;
  source?: string | null;
  primary_contact_id?: string;
  metadata?: Record<string, unknown> | null;
}

export interface OpportunityAdvance {
  to_stage_id: string;
}

// §10.6: Deal Terminal History — append-only opportunity_events.
//
// kind ∈ {open, reopen, won, lost}. Events are immutable; the only
// way to "edit" a close reason is to append another event that
// supersedes the prior one.

export type OpportunityEventKind = 'open' | 'reopen' | 'won' | 'lost';

export const LOSS_REASONS = [
  'Procurement freeze',
  'Lost to competitor',
  'No-decision / timing',
  'Budget cut',
  'Champion left',
] as const;

export type LossReason = (typeof LOSS_REASONS)[number];

export interface OpportunityEvent {
  id: number;
  opportunity_id: string;
  workspace_id: string;
  cycle: number;
  kind: OpportunityEventKind;
  reason?: string | null;
  loss_kind?: string | null;
  created_at: string;
  created_by?: string | null;
}

export interface OpportunityEventCreate {
  kind: OpportunityEventKind;
  reason?: string | null;
  // Required when kind === 'lost'. Must be one of LOSS_REASONS.
  loss_kind?: LossReason | null;
}

export interface OpportunityWithEvents extends Opportunity {
  events: OpportunityEvent[];
}

export interface OpportunityStageHistoryEntry {
  id: string;
  opportunity_id: string;
  from_stage_id?: string | null;
  to_stage_id: string;
  changed_by_user_id?: string | null;
  trigger: 'manual' | 'sequence' | 'api';
  changed_at: string;
}
